import React, { useState, useContext } from "react";
import { useNavigate } from "react-router";
import { AxiosError } from "axios";
import isEmpty from "lodash/isEmpty";
import HttpStatus from "@lib-shared/constants/http-status";
import { useEffectOnce } from "@lib-shared/hooks";
import { removeEmptyEntries } from "@lib-shared/lib";
import { ApplicationAction, BusinessPage, Messages } from "core/constants";
import { AppContext } from "core/context";
import { sessionManager } from "core/instances";
import { getErrorMessage, logApiError, UrlUtils } from "core/lib";
import getUserDetailsAsync, { userStatus } from "core/lib/get-user-details";
import { LocalStorageHelper } from "core/lib/local-storage-helper";
import signalRHelper from "core/lib/signal-r-helper";
import Style from "core/lib/style";
import UserPermissionsManager from "core/lib/user-permissions.manager";
import User from "core/models/user";
import { PermissionService } from "core/services/cef";
import { SignUpPrimaryUserRequest } from "core/services/cef/permission.service.types";
import Notice from "components/notice";
import AuthStyleWrapper from "components/ui/auth-style-wrapper";
import Button from "components/ui/form/button";

const ProcessAuthCode = () => {
    const appContext = useContext(AppContext);

    const [message, setMessage] = useState("Processing authentication");
    const [showSpinner, setShowSpinner] = useState(true);
    const [showRetryButton, setShowRetryButton] = useState(false);
    const failureUrl = BusinessPage.accountNotFound;
    const navigate = useNavigate()
    const defaultErrorMessage = "Sorry there has been an error";

    appContext.updateBodyCssName(Style.auth);
    appContext.updateTitle("");

    const _log = (...args: Array<any>) => {
        if (args.length === 1) {
            LocalStorageHelper.stampedLog(args[0]);
            return;
        }

        console.log(...args);
        LocalStorageHelper.log(...args);
    }

    const _gotoPage = (url: string) => {
        UrlUtils.removeQueryParamsFromUrl();

        _log(`Goto ${url}`);

        if (url.includes("http") || url.startsWith(UrlUtils.getRootPath())) {
            window.location.href = url;
            return;
        }

        navigate(url);
    }

    const _getReturnUrl = () => {
        const returnUrl = sessionManager.returnUrl;
        _log(`Return Url: ${returnUrl}`);

        return !returnUrl ? BusinessPage.dashboard : returnUrl;
    }

    const _handleReturnUrl = () => {
        const userAccess = sessionManager.accessToken;

        if (userAccess == null) {
            _log("Not getting customer details as there is no access");
            handleSignInFailure();
            return;
        }

        //Initialized before getting business user
        //because country code is needed for apis?
        // User.instance = getUser();

        const returnUrl = _getReturnUrl();

        const temp = returnUrl.split("action=");

        const action = temp.length > 1 ? temp[1] : null;

        switch (action) {
            case ApplicationAction.campaignSignUp:
                handleSignUp(returnUrl);
                break;

            case ApplicationAction.childSignUp:
                handleChildSignUp(returnUrl);
                break;

            case ApplicationAction.childIndependentSignUp:
                handleChildSignUp(returnUrl, true);
                break;

            case ApplicationAction.redirectOnly:
                redirectOnly(returnUrl);
                break;

            default:
                getUserDetails(returnUrl);
                break;
        }
    }

    const handleSignUp = async (returnUrl: string) => {
        _log("Handle sign up");

        const campaignData = sessionManager.campaignData;

        setMessage("Linking MultiChoice Account to Business Account.");

        try {
            const { firstName, lastName } = sessionManager.getNameOfUser();

            //V2 api currently not working
            const request: SignUpPrimaryUserRequest = {
                campaignSignup: true,
                encryptedCustomerNumber: campaignData.customerNumber,
                firstName: "",
                lastName: "",
            };

            const result = await new PermissionService().signUpPrimaryUser(request);
            // const result = await new PermissionService().linkUser(campaignData.customerNumber, firstName, lastName);

            handleLinkUpCustomerResult(result, returnUrl)
        } catch (error: any) {
            logApiError(error);

            if (error?.response?.data?.errorDescription?.includes("Duplicate user(s) found")) {
                getUserDetails(BusinessPage.dashboard);
                return;
            }

            const firstError = error?.response?.status === HttpStatus.forbidden
                ? "Your email address or phone number does not match the details we have for the account"
                : getErrorMessage(error, Messages.apiError);

            setMessage(firstError);

            setShowSpinner(false);
        }
    }

    const handleChildSignUp = async (returnUrl: string, isIndependentSignUp = false, customerNumber: number | undefined = undefined) => {
        _log("Handle child sign up");

        const campaignData = sessionManager.campaignData;

        setMessage("Your sign up has been sent for approval.");

        const custNumber = customerNumber ?? campaignData.customerNumber;

        try {
            const result = !isIndependentSignUp
                ? await new PermissionService().acceptInvitation(custNumber)
                : await UserPermissionsManager.registerChildUser(custNumber);

            await checkIfChildApproved(result, returnUrl);
        } catch (error) {
            const axiosError = error as AxiosError;

            if (axiosError?.response?.status === 404) {
                //The invite might have already been accepted
                getUserDetails(BusinessPage.dashboard);
            }

            if (axiosError?.response?.status === 403) {
                setMessage("There has been an error completing your sign up.");
                logApiError(error);
            }
        }
    }

    const checkIfChildApproved = async (result: any, returnUrl: string) => {
        const data = result != null ? result.data : null;

        const isSuccess = data != null && result.status === 200;

        if (isSuccess) {
            await checkChildSignIn(returnUrl);
            return;
        }

        let msg = "Error accepting invite";

        if (data.errorDescription != null) {
            msg += ` ${data.errorDescription}`;
        }

        setMessage(msg);

        setShowSpinner(false);
    }

    const handleLinkUpCustomerResult = (response: any, returnUrl: string) => {
        const responseStatus = response?.status ?? 500;

        const isLinked = responseStatus === 200;

        if (isLinked) {
            getUserDetails(returnUrl);
            return;
        }

        let msg = "Linking failed";

        const errorMessage = response?.data?.errorDescription;

        if (errorMessage) {
            msg += ` ${errorMessage}`;
        }

        setMessage(msg);

        setShowSpinner(false);
    }

    const handleSignInFailure = () => {
        _log(`Go to ${failureUrl}`);

        sessionManager.clear();

        navigate(failureUrl, { replace: true });
    }

    const checkChildSignIn = async (returnUrl: string) => {
        _log("Getting child user details");

        try {
            const { status } = await getUserDetailsAsync();

            if (status === userStatus.waitingForApproval) {
                navigate(BusinessPage.accountWaitingApproval, { replace: true });
                return;
            }

            const parentCiam = User.instance?.company?.parentCiam ?? "";

            signalRHelper.notifyPermissionUpdate(parentCiam);

            sessionManager.resetReturnUrl();

            _log("Redirecting...")
            _gotoPage(returnUrl);
        } catch (error) {
            logApiError(error);
            navigate(BusinessPage.accountWaitingApproval, { replace: true });
        }
    }

    const redirectOnly = (returnUrl: string) => {
        sessionManager.resetReturnUrl();

        _log("Redirecting...", returnUrl)
        _gotoPage(returnUrl);
    }

    const getUserDetails = async (returnUrl: string) => {
        _log("Getting user details");

        try {
            const { status, customerNumber } = await getUserDetailsAsync();

            if (status === userStatus.pendingRegistration) {
                handleChildSignUp(returnUrl, false, customerNumber);
                return;
            }

            if (status === userStatus.waitingForApproval) {
                navigate(BusinessPage.accountWaitingApproval, { replace: true });
                return;
            }

            if (status !== userStatus.active) {
                handleSignInFailure();
                return;
            }

            const user = sessionManager.loggedInUser;

            if (returnUrl.includes(BusinessPage.accountCreated) || (user?.companies.length ?? 0) === 1) {
                sessionManager.resetReturnUrl();

                _log("Redirecting...", returnUrl)
                _gotoPage(returnUrl);
                return;
            }

            _gotoPage(BusinessPage.chooseCompany);
        } catch (error: any) {
            if (error.response?.status === 404) {
                _gotoPage(BusinessPage.accountLinkUp);
                return;
            }

            logApiError(error);

            //handleSignInFailure();
            handleError(error, defaultErrorMessage);
        }
    }

    const FormattedMessage = () => {
        if (isEmpty(message)) {
            return null;
        }

        return <p dangerouslySetInnerHTML={{ __html: message }} />;
    }

    const retry = () => sessionManager.signIn(sessionManager.returnUrl);

    const exchangeCodeForTokenAsync = async (code: string) => {
        try {
            await sessionManager.verifyTokenAsync(code);
        } catch (error) {
            handleError(error, defaultErrorMessage);
            return;
        }

        //Don't await
        sessionManager.getCustomerDetailsAsync();

        //Handle child sign up scenarios
        _handleReturnUrl();
    }

    const handleError = (error: any, forceError?: string) => {
        _log("Error", error);

        let message = "";

        if (forceError) {
            message = forceError
        } else {
            const errors = [
                error?.request?.response?.error_description,
                error?.data?.error_description,
                error.message,
                error,
                "Error"
            ];

            message = removeEmptyEntries(errors)[0];

            console.log(errors);
        }

        setMessage(message);

        setShowRetryButton(true);
        setShowSpinner(false);
    }

    const getUrlSearchParams = () => {
        const href = window.location.href

        const index = href.indexOf("#token_type");

        if (index == -1) {
            return new URLSearchParams(href);
        }

        const tokens = href.substring(index) + 1;
        return new URLSearchParams(tokens);
    }

    useEffectOnce(() => {
        const onLoad = async () => {
            //const searchParams = new URLSearchParams(location.search);

            const href = window.location.href;

            if (!href.includes("token_type")) {
                return;
            }

            LocalStorageHelper.cleanUp();

            var params = getUrlSearchParams();
            const authCode = params.get("auth_code")?.toString() ?? "";

            try {
                await exchangeCodeForTokenAsync(authCode);
            } catch (error) {
                handleError(error);
            }

            return () => {
                _log("CleanUp ProcessAuthCode");
            };
        }

        onLoad();
    });

    return (
        <AuthStyleWrapper>
            <Notice showSpinner={showSpinner}>
                <>
                    <FormattedMessage />
                    {showRetryButton ? <Button text="Retry" onClick={retry} /> : null}
                </>
            </Notice>
        </AuthStyleWrapper>
    );
}

export default ProcessAuthCode;
